import { graphql } from 'gatsby';
import { lazy, Suspense, useMemo } from 'react';
import {
  StyledContainer,
  StyledContent,
  StyledDescription,
  StyledEyebrow,
  StyledHeadline,
  StyledIcon,
  StyledMediaContainer,
  StyledInfoContainer,
  StyledInfoContent,
  StyledImageElement,
  StyledLink,
} from './Tab.styles';
import Video from '@/components/Video/Video';
import NavigationDropdown from '@/components/NavigationDropdown/NavigationDropdown';

const N5050Block = lazy(() => import('@/components/N5050Block/N5050Block'));
const SingleMedia = lazy(() => import('@/components/SingleMedia/SingleMedia'));

interface TabProps extends Queries.TABFragment {
  readonly hasWhiteBackground: boolean;
}

/**
 * NOTE:
 *
 * Once all tabs have been migrated to use the new `content` element,
 * we can remove the old elements `headline`, `media`, `sub_headline`, `text`, `icon`,`cta` and `options`.
 * At that point, this component will simply render the `content` element, and keep the `tab_headline` for the tab label.
 *
 * All the styles and customization will then be removed from this component and it's `.styles.ts` file.
 *
 * Until then, we need to keep both implementations.
 */
const Tab = ({ elements, hasWhiteBackground }: TabProps) => {
  const { headline, media, sub_headline, text, icon, cta, options, content } = elements;
  const eyebrow = sub_headline?.value;
  const hasIcon = !!icon?.value;
  const mediaValue = media.value;
  const isImageFullWidth = options?.value?.includes('image_full_width') ?? false;
  const hasNewContent = typeof content?.value?.__typename === 'string';

  const link = useMemo(() => {
    switch (cta?.value?.__typename) {
      case 'kontent_item_link':
        return <StyledLink {...cta.value} variant="primary" />;
      case 'kontent_item_navigation_dropdown':
        return <NavigationDropdown {...cta.value} />;
      default:
        return null;
    }
  }, [cta?.value]);

  const hasDropdown = cta?.value?.__typename === 'kontent_item_navigation_dropdown';

  if (hasNewContent) {
    return (
      <Suspense>
        {content.value.__typename === 'kontent_item_n5050_block' && (
          <N5050Block {...content.value} />
        )}
        {content.value.__typename === 'kontent_item_single_media' && (
          <SingleMedia {...content.value} />
        )}
      </Suspense>
    );
  }

  return (
    <StyledContent>
      <StyledContainer isImageFullWidth={isImageFullWidth} hasWhiteBackground={hasWhiteBackground}>
        <StyledInfoContainer>
          {hasIcon && <StyledIcon {...icon.value} />}
          {eyebrow && <StyledEyebrow>{eyebrow}</StyledEyebrow>}
          <StyledInfoContent hasDropdown={hasDropdown}>
            <StyledHeadline>{headline.value}</StyledHeadline>
            <StyledDescription>{text.value}</StyledDescription>
          </StyledInfoContent>
          {link}
        </StyledInfoContainer>
        <StyledMediaContainer>
          {mediaValue.__typename === 'kontent_item_responsive_image' && (
            <StyledImageElement {...mediaValue} fit="contain" />
          )}
          {mediaValue.__typename === 'kontent_item_video' && <Video {...mediaValue} />}
        </StyledMediaContainer>
      </StyledContainer>
    </StyledContent>
  );
};

export default Tab;

export const fragment = graphql`
  fragment TAB on kontent_item_tab {
    elements {
      headline {
        value
      }
      icon {
        value {
          url
        }
      }
      media {
        value {
          __typename
          ...RESPONSIVE_IMAGE
          ...VIDEO
        }
      }
      sub_headline {
        value
      }
      tab_headline {
        value
      }
      text {
        value
      }
      cta {
        value {
          ...LINK
          ...NAVIGATION_DROPDOWN
        }
      }
      options {
        value
      }
      content {
        value {
          ...N5050_BLOCK
          ...SINGLE_MEDIA
        }
      }
    }
    system {
      id
    }
  }
`;
